import React from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { BranchProvider } from './contexts/BranchContext'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import { AnimatePresence, motion } from 'framer-motion'
import BottomNav from './components/BottomNav'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import ThinkinCodeFooter from './components/ThinkinCodeFooter'

// Pages
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import DashboardPage from './pages/DashboardPage'
import WashQueuePage from './pages/WashQueuePage'
import ScanPage from './pages/ScanPage'
import CustomerPage from './pages/CustomerPage'
import InventoryPage from './pages/InventoryPage'
import ExpensesPage from './pages/ExpensesPage'
import ReportsPage from './pages/ReportsPage'
import SubscriptionsPage from './pages/SubscriptionsPage'
import DealersPage from './pages/DealersPage'
import ProductsPage from './pages/ProductsPage'
import ProfilePage from './pages/ProfilePage'
import NotFoundPage from './pages/NotFoundPage'

// New POS & Management Pages
import POSPage from './pages/POSPage'
import ManualPOSPage from './pages/ManualPOSPage'
import SalesInvoicesPage from './pages/SalesInvoicesPage'
import AccountsPage from './pages/AccountsPage'
import DiscountLogsPage from './pages/DiscountLogsPage'
import RoleManagementPage from './pages/RoleManagementPage'
import UserManagementPage from './pages/UserManagementPage'
import BranchManagementPage from './pages/BranchManagementPage'
import CashUpsPage from './pages/CashUpsPage'
import VouchersPage from './pages/VouchersPage'
import WashCatalogPage from './pages/WashCatalogPage'
import WashHistoryPage from './pages/WashHistoryPage'

// Customer Pages
import CustomerDashboard from './pages/customer/CustomerDashboard'
import CustomerHistory from './pages/customer/CustomerHistory'
import CustomerProfile from './pages/customer/CustomerProfile'
import CustomerQRCode from './pages/customer/CustomerQRCode'
import CustomerSubscriptions from './pages/customer/CustomerSubscriptions'

// POS Module

function ManualPosRedirect() {
  const location = useLocation()
  return <Navigate to={`/pos${location.search || ''}`} replace />
}

function App() {
  const location = useLocation();

  // Only animate customer routes.
  // Use exact match or /customer/ prefix — avoids matching /customers (business page).
  const isCustomerRoute =
    location.pathname === '/customer' ||
    location.pathname.startsWith('/customer/');

  return (
    <AuthProvider>
      <BranchProvider>
        <div className="min-h-screen bg-gray-50">
          {isCustomerRoute ? (
            <>
              <AnimatePresence mode="wait" initial={false}>
                <Routes location={location} key={location.pathname}>
                  {/* Customer Routes with slide animation */}
                  <Route path="/customer" element={
                    <ProtectedRoute allowedRoles={['customer']}>
                      <motion.div
                        initial={{ x: 60, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -60, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                      >
                        <CustomerDashboard />
                      </motion.div>
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/history" element={
                    <ProtectedRoute allowedRoles={['customer']}>
                      <motion.div
                        initial={{ x: 60, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -60, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                      >
                        <CustomerHistory />
                      </motion.div>
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/profile" element={
                    <ProtectedRoute allowedRoles={['customer']}>
                      <motion.div
                        initial={{ x: 60, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -60, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                      >
                        <CustomerProfile />
                      </motion.div>
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/qr" element={
                    <ProtectedRoute allowedRoles={['customer']}>
                      <motion.div
                        initial={{ x: 60, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -60, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                      >
                        <CustomerQRCode />
                      </motion.div>
                    </ProtectedRoute>
                  } />
                  <Route path="/customer/subscriptions" element={
                    <ProtectedRoute allowedRoles={['customer']}>
                      <motion.div
                        initial={{ x: 60, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -60, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                      >
                        <CustomerSubscriptions />
                      </motion.div>
                    </ProtectedRoute>
                  } />
                  {/* 404 fallback for customer routes */}
                  <Route path="*" element={<Navigate to="/customer" replace />} />
                </Routes>
              </AnimatePresence>
              <ThinkinCodeFooter className="pb-24" />
              <BottomNav />
            </>
          ) : (
            <Routes location={location}>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              {/* Protected Business Routes */}
              <Route path="/" element={
                <ProtectedRoute allowedRoles={['owner', 'manager', 'staff']}>
                  <Layout />
                </ProtectedRoute>
              }>
                {/* Dashboard */}
                <Route index element={<DashboardPage />} />
                {/* Operations */}
                <Route path="wash-queue" element={<WashQueuePage />} />
                <Route
                  path="wash-history"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'manager', 'staff']}>
                      <WashHistoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="scan" element={<ScanPage />} />
                {/* POS = checkout. Payment queue at /pos-queue (and /pos/queue). */}
                <Route path="pos" element={<ManualPOSPage />} />
                <Route path="pos-queue" element={<POSPage />} />
                <Route path="pos/queue" element={<Navigate to="/pos-queue" replace />} />
                <Route path="pos/:washId" element={<ManualPOSPage />} />
                <Route path="manual-pos" element={<ManualPosRedirect />} />
                {/* Sales & Finance */}
                <Route path="sales-invoices" element={<SalesInvoicesPage />} />
                <Route path="accounts" element={<AccountsPage />} />
                <Route
                  path="cash-ups"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'manager', 'staff']}>
                      <CashUpsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="vouchers"
                  element={
                    <ProtectedRoute allowedRoles={['owner', 'manager']}>
                      <VouchersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="expenses"
                  element={
                    <ProtectedRoute
                      allowedRoles={['owner', 'manager', 'staff']}
                      requiredPermission="manage_expenses"
                    >
                      <ExpensesPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="reports" element={<ReportsPage />} />
                {/* Management */}
                <Route path="customers" element={<CustomerPage />} />
                <Route
                  path="products"
                  element={
                    <ProtectedRoute
                      allowedRoles={['owner', 'manager', 'staff']}
                      requiredPermission="manage_products"
                    >
                      <ProductsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="inventory"
                  element={
                    <ProtectedRoute
                      allowedRoles={['owner', 'manager', 'staff']}
                      requiredPermission="manage_inventory"
                    >
                      <InventoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="subscriptions"
                  element={
                    <ProtectedRoute
                      allowedRoles={['owner', 'manager', 'staff']}
                      requiredPermission="manage_subscriptions"
                    >
                      <SubscriptionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="wash-catalog"
                  element={
                    <ProtectedRoute
                      allowedRoles={['owner', 'manager', 'staff']}
                      requiredPermission="manage_wash_catalog"
                    >
                      <WashCatalogPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="dealers"
                  element={
                    <ProtectedRoute
                      allowedRoles={['owner', 'manager', 'staff']}
                      requiredPermission="manage_dealers"
                    >
                      <DealersPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="discount-logs" element={<DiscountLogsPage />} />
                <Route path="branch-management" element={<BranchManagementPage />} />
                <Route path="role-management" element={<RoleManagementPage />} />
                <Route path="user-management" element={<UserManagementPage />} />
                {/* User */}
                <Route path="profile" element={<ProfilePage />} />
              </Route>
              {/* 404 Page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          )}
          
          {/* PWA Install Prompt */}
          <PWAInstallPrompt />
        </div>
      </BranchProvider>
    </AuthProvider>
  )
}

export default App
